type Attrs = Record<string, string | undefined>;
type Child = Node | string | null | undefined | false;

/**
 * Tiny element helper. Everything is set through textContent and setAttribute, so no value
 * rendered by this app can become markup.
 */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  for (const [key, value] of Object.entries(attrs)) {
    if (value !== undefined) {
      node.setAttribute(key, value);
    }
  }

  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    node.append(typeof child === 'string' ? document.createTextNode(child) : child);
  }

  return node;
}

export type StatusTone = 'live' | 'pending' | 'blocked' | 'held';

export function statusPill(tone: StatusTone, label: string): HTMLElement {
  return el('span', { class: `pill pill--${tone}` }, label);
}

export function definitionList(rows: readonly (readonly [string, string])[]): HTMLElement {
  const list = el('dl', { class: 'facts' });
  for (const [term, value] of rows) {
    list.append(el('dt', {}, term), el('dd', {}, value));
  }
  return list;
}

export function card(options: {
  readonly title: string;
  readonly tone: StatusTone;
  readonly toneLabel: string;
  readonly body: string;
  readonly facts?: readonly (readonly [string, string])[];
}): HTMLElement {
  return el(
    'article',
    { class: 'card' },
    el(
      'header',
      { class: 'card__head' },
      el('h3', {}, options.title),
      statusPill(options.tone, options.toneLabel),
    ),
    el('p', { class: 'card__body' }, options.body),
    options.facts && options.facts.length > 0 ? definitionList(options.facts) : null,
  );
}
